import React, {
  useEffect,
  useState,
} from 'react';

import { Popover } from 'react-tiny-popover';
import styled from 'styled-components';

const StyledContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: ${({ compactMode }) => (compactMode ? '100%' : '100%')};
  height: ${({ compactMode }) => (compactMode ? '100%' : 'auto')};
  border: ${({ borderWidth }) => (borderWidth !== undefined ? `${borderWidth}px` : '1px')} solid ${({ borderColor }) => borderColor || '#777'};
  background-color: ${({ backgroundColor }) => backgroundColor || 'transparent'};
  display: flex;
  align-items: center;
  gap: ${({ compactMode }) => (compactMode ? '4px' : '0.5em')};
  padding: ${({ compactMode }) => (compactMode ? '2px 4px' : '0.5em')};
  border-radius: 0.25em;
  outline: none;
  overflow: hidden;
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  box-sizing: border-box;

  &:focus {
    border-color: ${({ primaryColor, disabled }) => (!disabled ? primaryColor : 'none')};
  }
  border-radius: ${(props) => (props.borderRadius ? `${props.borderRadius}px` : '')}
`;

const StyledValue = styled.div`
  flex-grow: 1;
  display: flex;
  gap: 0.5em;
  flex-wrap: wrap;
`;

const StyledOptions = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 15em;
  overflow-y: auto;
  border: 0.05em solid ${({ borderColor }) => borderColor || '#fff'};
  border-radius: 0.25em;
  width: 100%;
  background-color: 'white';
`;

const getBackgroundColor = (isHighlighted, isSelected) => {
  if (isHighlighted) return 'hsl(200, 100%, 50%)';
  if (isSelected) return 'white';
  return 'white';
};

const StyledOption = styled.li`
  padding: 0.25em 0.5em;
  cursor: pointer;

  background-color: ${({ isHighlighted, isSelected }) => getBackgroundColor(isHighlighted, isSelected)};
  color: ${({ isHighlighted }) => (isHighlighted ? 'white' : 'inherit')};

  &:hover {
    background-color: ${({ highlightColor }) => highlightColor || 'hsl(200, 100%, 50%)'};
    color: white;
  }
`;

const StyledOptionBadge = styled.span`
  display: flex;
  align-items: center;
  color: ${({ backgroundColor }) => getTextColor(backgroundColor)};
  background-color: ${({ backgroundColor }) => backgroundColor || '#f0f0f0'};
  border-radius: ${(props) => (props.borderRadius ? `${props.borderRadius}px` : '8px')};
  padding: 1px 6px;
  gap: 0.25em;
  cursor: pointer;
  outline: none;
  font-size: ${({ fontSize }) => (fontSize ? `${fontSize}px` : '12px')};
  line-height: 1.2;
`;

const getTextColor = (bgColor) => {
  if (!bgColor) return '#000'; // Default to black if no background color

  // Convert hex to RGB
  const r = parseInt(bgColor.substring(1, 3), 16);
  const g = parseInt(bgColor.substring(3, 5), 16);
  const b = parseInt(bgColor.substring(5, 7), 16);

  // Calculate luminance
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

  // Return white for dark backgrounds, black for light backgrounds
  return luminance > 150 ? '#000' : '#FFF';
};

const MultiSelect = ({
  multiple = false,
  value,
  onChange,
  onBlur,
  onFocus,
  options,
  disabled,
  borderColor,
  borderWidth,
  borderRadius,
  primaryColor,
  highlightColor,
  selectColor,
  fontSize,
  backgroundColor,
  styles,
  openPopoverFromParent,
  clearParentPopoverState,
  compactMode = false,
}) => {

  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const selectOption = (option) => {
    if (multiple) {
      onChange(
        value.includes(option) ? value.filter((v) => v !== option) : [...value, option]
      );
    } else if (option !== value) {
      onChange(option);
    }
  };

  const isOptionSelected = (option) => (multiple ? value.includes(option) : option === value);

  useEffect(() => {
    if (disabled) return;

    if (openPopoverFromParent) {
      setIsOpen(true);
    }

    if (isOpen) {

      setHighlightedIndex(0);
      /**
       * Logic below ensures that the popover is closed when user clicks outside of it
       *
       * When user clicks on table multiSelect cell, openPopoverFromParent is set to true and the popover is opened
       * But as soon as this is done, the parent popover state is cleared to prevent the login in line 155 from running
       *
       */
      if (clearParentPopoverState && openPopoverFromParent) clearParentPopoverState();
    }

  }, [isOpen, openPopoverFromParent, disabled]);

  const renderValue = () => {
    if (multiple) {
      return value.map((v) => (
        <StyledOptionBadge
          key={v.value}
          borderColor={borderColor}
          borderRadius={borderRadius}
          fontSize={fontSize}
          backgroundColor={v.backgroundColor}
          style={styles}
          // Disable interaction when readonly
          onClick={(e) => {
            if (!disabled) {
              e.stopPropagation();
              selectOption(v);
            }
          }}
        >
          {v.label}
        </StyledOptionBadge>
      ));
    } else {
      return value?.label || '';
    }
  };

  return (
    <Popover
      isOpen={!disabled && isOpen}
      positions={['bottom']}
      onClickOutside={() => !disabled && setIsOpen(false)}
      content={(
        <StyledOptions isOpen={!disabled && isOpen} borderColor={borderColor}>
          {options.map((option, i) => (
            <StyledOption
              key={option.value}
              isHighlighted={i === highlightedIndex}
              isSelected={isOptionSelected(option)}
              highlightColor={highlightColor}
              selectColor={selectColor}
              onClick={(e) => {
                if (disabled) return;
                e.stopPropagation();
                selectOption(option);
                setIsOpen(false);
                onBlur(value);
              }}
              onMouseEnter={() => setHighlightedIndex(i)}
            >
              {option.label}
            </StyledOption>
          ))}
        </StyledOptions>
      )}
      containerStyle={{ zIndex: 10000000000, borderRadius: 8, width: '15%' }}
      padding={2}
    >
      <StyledContainer
        tabIndex={0}
        borderColor={borderColor}
        borderWidth={borderWidth}
        borderRadius={borderRadius}
        primaryColor={primaryColor}
        compactMode={compactMode}
        valueCount={value?.length || 0}
        backgroundColor={backgroundColor}
        disabled={disabled}
        onClick={() => {
          if (!disabled) setIsOpen((prev) => !prev);
        }}
        onFocus={(e) => {
          if (!disabled && !isOpen) onFocus(e);
        }}
      >
        <StyledValue>{renderValue()}</StyledValue>
      </StyledContainer>
    </Popover>
  );
};

export default MultiSelect;
